/**
 * IndexedDB schema.
 *
 * The prototype used two stores — `suppliers` keyed by id, and `meta` holding
 * the whole fairs array as one blob. That does not survive production: fairs
 * need per-record sync, media needs to be Blobs rather than base64 inside the
 * record, and pending writes need a durable queue. So five stores:
 *
 *   fairs      per-record, so a fair can sync independently
 *   suppliers  products / contacts / followUps / review stay embedded, matching
 *              the prototype's object shape and the server's Mongo document
 *   media      Blobs — never base64, never inside a record (Mongo caps at 16MB
 *              and the spec allows 25MB videos)
 *   outbox     durable, ordered queue of local mutations awaiting sync
 *   meta       settings, currentFair, lastSyncAt, flags
 *
 * Bump DB_VERSION and add an `upgrades` entry to change this. Never edit an
 * existing upgrade step — a buyer mid-trip has the old version on their phone.
 */

/**
 * The store is PARTITIONED PER IDENTITY, and the partition key is the database
 * NAME rather than a field on the records.
 *
 * It shipped as a single database for the whole origin, which meant two
 * accounts on one phone shared one local store. Logging in as someone else
 * showed the previous buyer's fairs; worse, their still-pending outbox drained
 * under the new account's token, so the server faithfully stamped one buyer's
 * suppliers as another's. The server was never at fault — every query in
 * FairTrackerService is scoped by (spaceId, ownerUserId), and ownerUserId is
 * pinned to the authenticated user instead of being read off the request.
 *
 * So the local name is keyed on exactly the pair the server scopes on. Each
 * partition gets its own fairs, suppliers, media, outbox, settings AND
 * lastSyncAt, which makes the isolation structural rather than a filter someone
 * can forget to apply on a new query. It also means an un-synced booth capture
 * survives a logout and is still waiting when that account returns — which a
 * wipe-on-logout fix would have destroyed.
 *
 * DB_VERSION is deliberately NOT bumped: the shape inside a database is
 * unchanged, so a new partition just runs the v1 upgrade and gets five stores.
 */
export const DB_PREFIX = 'beenexa.fairtracker';

/**
 * The pre-partition name. Referenced ONLY by the one-time adoption in db.js,
 * which hands whatever is in it to the first account to sign in and then
 * deletes it. It is also the value of DB_PREFIX — harmless, because every
 * partitioned name carries a `::userId::spaceId` suffix and so can never
 * collide with it.
 */
export const LEGACY_DB_NAME = 'beenexa.fairtracker';

/**
 * The database name for one identity, or null when there is nothing to scope
 * to — a logged-out app must not be able to open anybody's partition.
 *
 * The spaceId fallback mirrors the server's own chain (companyId ?:
 * personalSpaceId ?: userId, per SpaceService.homeSpaceId), so a standalone
 * individual account settles in one stable partition rather than migrating
 * between two the moment a companyId appears on its userInfo.
 */
export function dbNameFor(identity) {
  const userId = identity && identity.userId;
  const spaceId = (identity && identity.spaceId) || userId;
  if (!userId || !spaceId) return null;
  return DB_PREFIX + '::' + userId + '::' + spaceId;
}

export const DB_VERSION = 1;

export const STORE = {
  FAIRS: 'fairs',
  SUPPLIERS: 'suppliers',
  MEDIA: 'media',
  OUTBOX: 'outbox',
  META: 'meta',
};

/** Every store in the database, for the whole-partition passes (adoption). */
export const ALL_STORES = [STORE.FAIRS, STORE.SUPPLIERS, STORE.MEDIA, STORE.OUTBOX, STORE.META];

/** meta keys, so a typo can't silently read undefined. */
export const META = {
  SETTINGS: 'settings',
  CURRENT_FAIR: 'currentFair',
  LAST_SYNC_AT: 'lastSyncAt',
  WELCOMED: 'welcomed',
  PROFILE_PREFILLED: 'profilePrefilled',
  /** Set once a partition has taken over the pre-partition database. */
  ADOPTED_LEGACY: 'adoptedLegacy',
};

/**
 * Applied in order for each version step. `db` is the IDBDatabase and `tx` the
 * versionchange transaction.
 */
export const upgrades = [
  {
    version: 1,
    apply(db) {
      const fairs = db.createObjectStore(STORE.FAIRS, { keyPath: 'id' });
      fairs.createIndex('updatedAt', 'updatedAt');

      const suppliers = db.createObjectStore(STORE.SUPPLIERS, { keyPath: 'id' });
      suppliers.createIndex('fairId', 'fairId');
      suppliers.createIndex('updatedAt', 'updatedAt');

      const media = db.createObjectStore(STORE.MEDIA, { keyPath: 'id' });
      media.createIndex('supplierId', 'supplierId');
      // Only rows awaiting upload carry pendingUpload: 1. IndexedDB skips
      // records whose indexed key is undefined, so this index holds exactly
      // the upload queue and nothing else.
      media.createIndex('pendingUpload', 'pendingUpload');

      // seq is monotonic and the queue is drained in seq order, which is what
      // makes replay deterministic.
      db.createObjectStore(STORE.OUTBOX, { keyPath: 'seq', autoIncrement: true });

      db.createObjectStore(STORE.META, { keyPath: 'k' });
    },
  },
];
